<template>
  <themed-card>
    <template #header>
      <h2 class="text-xl font-semibold">Invoice History</h2>
    </template>

    <!-- Invoices Table -->
    <div class="invoice-table-wrapper">
      <themed-table
        :data="invoices"
        :columns="columns"
        :loading="isLoading"
        striped
        hoverable
      >
        <template #column-created="{ value }">
          {{ formatDate(value) }}
        </template>

        <template #column-number="{ row }">
          {{ row.number || row.id }}
        </template>

        <template #column-amount_due="{ row }">
          {{ formatCurrency(row.amount_due ?? row.total, row.currency) }}
        </template>

        <template #actions="{ row }">
          <div class="invoice-actions">
            <themed-button
              size="sm"
              variant="ghost"
              @click="downloadInvoicePdf(row.id)"
              :disabled="isDownloading === row.id"
              aria-label="Download PDF"
              title="Download PDF"
            >
              <theme-icon icon="download" type="icons" />
            </themed-button>
            <a
              v-if="row.hosted_invoice_url"
              :href="row.hosted_invoice_url"
              target="_blank"
              rel="noopener"
              class="invoice-link"
              aria-label="Open hosted invoice"
              title="Open hosted invoice"
            >
              <theme-icon icon="external-link" type="icons" />
            </a>
          </div>
        </template>

        <template #empty-state>
          <p class="text-muted-foreground text-center py-4">
            No invoices found.
          </p>
        </template>
      </themed-table>
    </div>

    <!-- Load More -->
    <div v-if="hasMore" class="invoice-load-more">
      <themed-button
        variant="outline"
        @click="loadMore"
        :disabled="isLoading"
      >
        {{ isLoading ? 'Loading...' : 'Load More' }}
      </themed-button>
    </div>
  </themed-card>
</template>

<script>
import ThemedCard from '../ThemedCard.vue';
import ThemedTable from '../ThemedTable.vue';
import ThemedButton from '../ThemedButton.vue';
import ThemeIcon from '../ThemeIcon.vue';

export default {
  name: 'InvoiceHistory',
  components: {
    ThemedCard,
    ThemedTable,
    ThemedButton,
    ThemeIcon
  },
  data() {
    return {
      isLoading: false,
      isDownloading: null,
      invoices: [],
      hasMore: false,
      lastInvoiceId: null,
      columns: [
        { field: 'created', label: 'Date', sortable: true },
        { field: 'number', label: 'Invoice #', sortable: false },
        { field: 'amount_due', label: 'Amount', sortable: true },
        { type: 'actions', label: 'Actions' }
      ]
    };
  },
  async mounted() {
    await this.fetchInvoices();
  },
  methods: {
    unwrap(res) {
      const root = res?.data ?? res ?? null;
      if (!root) return null;
      if (Array.isArray(root)) return root;
      if (Array.isArray(root.data)) return root.data;
      if (Array.isArray(root.items)) return root.items;
      return root;
    },
    getHasMore(res) {
      const root = res?.data ?? res ?? {};
      return Boolean(root.has_more ?? root.hasMore ?? false);
    },
    getLastId(list) {
      if (!list || list.length === 0) return null;
      return list[list.length - 1].id ?? null;
    },
    async fetchInvoices() {
      if (this.isLoading) return;
      this.isLoading = true;
      try {
        const params = { limit: 10, starting_after: this.lastInvoiceId };
        const res = await this.$store.dispatch('billing/fetchInvoices', params);

        const list = this.unwrap(res) || [];
        const items = Array.isArray(list) ? list : (list?.data ?? list?.items ?? []);

        if (!Array.isArray(items)) {
          throw new Error('Unexpected invoices response shape.');
        }

        this.invoices = [...this.invoices, ...items];
        this.hasMore = this.getHasMore(res);
        this.lastInvoiceId = this.getLastId(this.invoices);
      } catch (error) {
        console.error('fetchInvoices error:', error);
        this.$toast?.error?.('Failed to load invoices');
      } finally {
        this.isLoading = false;
      }
    },
    async loadMore() {
      await this.fetchInvoices();
    },
    formatDate(unixSeconds) {
      const date = new Date((Number(unixSeconds) || 0) * 1000);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    },
    formatCurrency(amount, currency = 'USD') {
      const cents = Number(amount || 0);
      const code = String(currency || 'USD').toUpperCase();
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: code,
      }).format(cents / 100);
    },
    async downloadInvoicePdf(invoiceId) {
      this.isDownloading = invoiceId;
      try {
        await this.$store.dispatch('billing/downloadInvoicePdf', invoiceId);
      } catch (error) {
        console.error('downloadInvoicePdf error:', error);
        this.$toast?.error?.('Failed to download invoice');
      } finally {
        this.isDownloading = null;
      }
    },
  },
};
</script>
